import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";

// Section TVA — Suivi des livreurs Nold Go ayant atteint le seuil 20 000€ de CA cumulé.
// Statuts : not_required (< seuil) · required (seuil franchi, email envoyé) · provided (n° soumis, en attente admin) · verified (validé par admin).
export default function VatSection() {
  const [list, setList] = useState({ items: [], count: 0, threshold_eur: 20000 });
  const [status, setStatus] = useState("all"); // all | required | provided | verified | not_required
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);

  const load = async (s = status) => {
    setLoading(true);
    try {
      const params = s === "all" ? {} : { params: { status: s } };
      const { data } = await api.get("/admin/vat/drivers", params);
      setList(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger la liste");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(status); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status]);
  const scanNow = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/vat/scan-now");
      toast.success(`Scan terminé — ${data.scanned} livreurs analysés, ${data.changed} mis à jour`);
      await load(status);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du scan");
    } finally {
      setScanning(false);
    }
  };

  const verify = async (driverId, currentVat) => {
    let vat = currentVat;
    if (!vat || vat.length < 4) {
      vat = window.prompt("Numéro de TVA à valider (ex: BE0123456789) :", "");
      if (!vat) return;
    }
    if (!window.confirm(`Valider le n° TVA "${vat}" pour ce livreur ?`)) return;
    try {
      await api.post(`/admin/vat/${driverId}/verify`, { vat_number: vat });
      toast.success("N° TVA validé ✓");
      await load(status);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation impossible");
    }
  };

  const reset = async (driverId) => {
    if (!window.confirm("Remettre ce livreur en statut TVA « non requis » ?")) return;
    try {
      await api.post(`/admin/vat/${driverId}/reset`);
      toast.success("Statut TVA remis à zéro");
      await load(status);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset impossible");
    }
  };

  const deleteDriver = async (driverId, name) => {
    const confirmMsg = `⚠️ SUPPRESSION DÉFINITIVE\n\nLivreur : ${name || driverId}\n\nCela va supprimer le compte livreur, ses courses et son historique.\nCette action est IRRÉVERSIBLE.\n\nTaper "SUPPRIMER" pour confirmer :`;
    const answer = window.prompt(confirmMsg, "");
    if ((answer || "").trim().toUpperCase() !== "SUPPRIMER") return;
    try {
      await api.delete(`/admin/drivers/${driverId}?force=true&reason=admin_vat_cleanup`);
      toast.success("Livreur supprimé");
      await load(status);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    }
  };

  const statusBadge = (s) => {
    const map = {
      not_required: { label: "Non requis", color: "bg-[#F0EBE1] text-[#75807A]" },
      required: { label: "🔔 TVA requise", color: "bg-[#F26419]/15 text-[#F26419]" },
      provided: { label: "📩 En attente admin", color: "bg-[#3B82F6]/15 text-[#3B82F6]" },
      verified: { label: "✓ Vérifié", color: "bg-[#16a34a]/15 text-[#16a34a]" },
    };
    const m = map[s] || map.not_required;
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold ${m.color}`}>{m.label}</span>;
  };

  const fmt = (v) => (v ? new Date(v).toLocaleDateString("fr-BE", { day: "2-digit", month: "short", year: "numeric" }) : "—");

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-head font-black text-2xl text-[#1A1A1A]">🧾 TVA livreurs Nold Go</h2>
          <p className="text-sm text-[#75807A] mt-1">
            Seuil déclencheur : <b>{Number(list.threshold_eur || 20000).toLocaleString("fr-BE")} €</b> de CA brut cumulé.
            Le watcher scanne automatiquement toutes les heures. Aucun blocage : les livreurs continuent à travailler.
          </p>
        </div>
        <Button data-testid="vat-scan-now-btn" onClick={scanNow} disabled={scanning}
          className="h-10 rounded-xl bg-gradient-to-r from-[#F26419] to-[#D94F2B] text-white font-black text-sm whitespace-nowrap disabled:opacity-40">
          {scanning ? "Scan en cours…" : "🔍 Scanner maintenant"}
        </Button>
      </div>

      {/* Onglets */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { id: "all", label: "Tous" },
          { id: "required", label: "🔔 TVA requise", color: "text-[#F26419] border-[#F26419]/40" },
          { id: "provided", label: "📩 En attente admin", color: "text-[#3B82F6] border-[#3B82F6]/40" },
          { id: "verified", label: "✓ Vérifié", color: "text-[#16a34a] border-[#16a34a]/40" },
          { id: "not_required", label: "Non requis" },
        ].map((t) => (
          <button key={t.id}
            data-testid={`vat-tab-${t.id}`}
            onClick={() => setStatus(t.id)}
            className={`h-9 px-3 rounded-lg text-xs font-bold border-2 ${status === t.id ? "bg-[#F26419] text-white border-[#F26419]" : `bg-white ${t.color || "text-[#1A1A1A] border-[#E2DFD8]"} hover:bg-[#F0EBE1]`}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Liste */}
      {loading && <div className="text-center py-8 text-[#75807A] italic">Chargement…</div>}
      {!loading && list.items.length === 0 && (
        <div data-testid="vat-empty" className="text-center py-12 bg-white rounded-xl border border-[#E2DFD8] text-[#75807A]">
          Aucun livreur dans cette catégorie.
        </div>
      )}
      {!loading && list.items.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E2DFD8] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F0EBE1] text-[#3B423E]">
                <tr>
                  <th className="text-left px-3 py-2.5 font-bold">Livreur</th>
                  <th className="text-right px-3 py-2.5 font-bold">CA cumulé</th>
                  <th className="text-left px-3 py-2.5 font-bold">Statut</th>
                  <th className="text-left px-3 py-2.5 font-bold">N° TVA</th>
                  <th className="text-left px-3 py-2.5 font-bold">Email envoyé</th>
                  <th className="text-right px-3 py-2.5 font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((u) => (
                  <tr key={u.id} data-testid={`vat-row-${u.id}`} className="border-t border-[#F0EBE1] hover:bg-[#FDF8F0]/50">
                    <td className="px-3 py-2.5">
                      <div className="font-bold text-[#1A1A1A]">{u.name || "—"}</div>
                      <div className="text-xs text-[#75807A]">{u.email || u.phone || u.id.slice(0, 8)}</div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-black tabular-nums">
                      {Number(u.cumulative_gross_eur || 0).toLocaleString("fr-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </td>
                    <td className="px-3 py-2.5">{statusBadge(u.vat_status)}</td>
                    <td className="px-3 py-2.5 font-mono text-xs">{u.vat_number || "—"}</td>
                    <td className="px-3 py-2.5 text-xs text-[#75807A]">{fmt(u.vat_email_sent_at)}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      {u.vat_status !== "verified" && (
                        <button
                          data-testid={`vat-verify-${u.id}`}
                          onClick={() => verify(u.id, u.vat_number)}
                          className="h-8 px-3 rounded-lg bg-[#16a34a] hover:bg-[#128a3e] text-white text-xs font-bold mr-1.5">
                          ✓ Valider
                        </button>
                      )}
                      {u.vat_status !== "not_required" && (
                        <button
                          data-testid={`vat-reset-${u.id}`}
                          onClick={() => reset(u.id)}
                          className="h-8 px-3 rounded-lg bg-white border-2 border-[#75807A]/30 text-[#75807A] hover:bg-[#F0EBE1] text-xs font-bold mr-1.5">
                          Reset
                        </button>
                      )}
                      <button
                        data-testid={`vat-delete-${u.id}`}
                        onClick={() => deleteDriver(u.id, u.name)}
                        title="Supprimer définitivement ce livreur (compte + courses)"
                        className="h-8 px-3 rounded-lg bg-white border-2 border-[#D32F2F]/40 text-[#D32F2F] hover:bg-[#D32F2F]/10 text-xs font-bold">
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
